/**
 * Client-side LiteRT.js inference for the annotate modal when a model has
 * a bare .tflite export (issue #40). Avoids starting a run_model worker job.
 */
import { isDownloadableTfliteFile } from '@/utils/modelExportFiles';
import { isClassificationModel } from '@/utils/modelExportTypes';
import { protectedAssetUrlWithToken } from '@/utils/annotatePreload';
import {
  ensureLiteRt,
  loadDetectionModel,
  fetchTfliteBytes,
  getModelInputSpec,
  letterboxDraw,
  classificationDraw,
  runDetection,
  runClassification,
} from '@/utils/liveDemoInference';
import {
  labelsFromModelMeta,
  resolveBrowserModelLabels,
} from '@/utils/modelLabels';

/** Same palette as api-echo DefaultAnnotateColors. */
export const ANNOTATE_LITERT_COLORS = [
  [249, 65, 68], [243, 114, 44], [248, 150, 30], [67, 170, 139],
  [249, 132, 74], [249, 199, 79], [144, 190, 109], [77, 144, 142],
];

function parseMeta(model) {
  if (!model || !model.meta) {
    return {};
  }
  try {
    const meta = typeof model.meta === 'string' ? JSON.parse(model.meta) : model.meta;
    return meta && typeof meta === 'object' ? meta : {};
  } catch {
    return {};
  }
}

function basename(storedPath) {
  const parts = String(storedPath || '').split('/');
  return parts[parts.length - 1] || '';
}

/**
 * True when model.meta.files lists a bare .tflite suitable for LiteRT.js.
 * Zip-only exports fall back to the server runner.
 */
export function modelHasBrowserTflite(model) {
  const meta = parseMeta(model);
  const files = Array.isArray(meta.files) ? meta.files : [];
  return files.some((f) => isDownloadableTfliteFile({ name: basename(f) }));
}

export function getBrowserTfliteBasename(model) {
  const meta = parseMeta(model);
  const files = Array.isArray(meta.files) ? meta.files : [];
  for (const f of files) {
    const name = basename(f);
    if (isDownloadableTfliteFile({ name })) {
      return name;
    }
  }
  return '';
}

export function browserTfliteUrl(model, projectId, token) {
  const name = getBrowserTfliteBasename(model);
  if (!name || !projectId) {
    return '';
  }
  const base = `/api/protected/models/${encodeURIComponent(projectId)}/${encodeURIComponent(name)}`;
  return protectedAssetUrlWithToken(base, token);
}

/** Synchronous labels from meta only (may be empty — use resolveBrowserModelLabels at run time). */
export function modelLabelNames(model) {
  return labelsFromModelMeta(model);
}

function parseMetaBool(value) {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return Boolean(value);
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['yes', 'true', '1'].includes(normalized)) {
      return true;
    }
    if (['no', 'false', '0', ''].includes(normalized)) {
      return false;
    }
  }
  return Boolean(value);
}

function rectTrainingEnabled(model, meta) {
  if (meta && Object.prototype.hasOwnProperty.call(meta, 'rect_training')) {
    return parseMetaBool(meta.rect_training);
  }
  const name = (model && model.name) || '';
  return name.indexOf('*rect') !== -1;
}

/**
 * Fallback [height, width] for getModelInputSpec when the TFLite signature
 * has no spatial shape. Scalar ``image_size`` with rect_training is wide:
 * height = size/2, width = size (matches EdgeYOLO train.yaml / export).
 */
export function modelImageSize(model) {
  const meta = parseMeta(model);
  const raw = meta.image_size || meta.imgsz || meta.input_size;
  if (Array.isArray(raw) && raw.length >= 2) {
    return [Number(raw[0]) || 640, Number(raw[1]) || 640];
  }
  if (Array.isArray(raw) && raw.length === 1) {
    const n = Number(raw[0]) || 640;
    return rectTrainingEnabled(model, meta) ? [Math.floor(n / 2) || n, n] : [n, n];
  }
  if (typeof raw === 'number' && raw > 0) {
    return rectTrainingEnabled(model, meta) ? [Math.floor(raw / 2) || raw, raw] : [raw, raw];
  }
  if (isClassificationModel(model)) {
    return [224, 224];
  }
  return [640, 640];
}

function clamp01(v) {
  if (!Number.isFinite(v)) {
    return 0;
  }
  return Math.min(1, Math.max(0, v));
}

/**
 * Map letterboxed detection pixels back to normalized xywh on the source image.
 */
export function detectionToNormalizedXywh(det, prep) {
  const invScale = prep.scale > 0 ? 1 / prep.scale : 1;
  const srcW = prep.srcW || 1;
  const srcH = prep.srcH || 1;
  const x1 = ((det.x1 - prep.padX) * invScale) / srcW;
  const y1 = ((det.y1 - prep.padY) * invScale) / srcH;
  const x2 = ((det.x2 - prep.padX) * invScale) / srcW;
  const y2 = ((det.y2 - prep.padY) * invScale) / srcH;
  const left = clamp01(Math.min(x1, x2));
  const top = clamp01(Math.min(y1, y2));
  const right = clamp01(Math.max(x1, x2));
  const bottom = clamp01(Math.max(y1, y2));
  return [left, top, clamp01(right - left), clamp01(bottom - top)];
}

function loadHtmlImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load asset image for LiteRT inference'));
    img.src = url;
  });
}

/**
 * Run LiteRT on an asset image URL; returns annotation-shaped objects
 * { coords, conf, label, type, format } ready for _annotationFromServer.
 */
export async function runAnnotateLiteRtInference({
  model,
  assetImageUrl,
  projectId,
  token,
  confThreshold = 0.25,
}) {
  const tfliteUrl = browserTfliteUrl(model, projectId, token);
  if (!tfliteUrl) {
    throw new Error('No TFLite file available for browser inference');
  }
  const imageUrl = protectedAssetUrlWithToken(assetImageUrl, token);
  await ensureLiteRt();
  const tfliteBytes = await fetchTfliteBytes(tfliteUrl);
  const compiled = await loadDetectionModel(tfliteBytes);
  const img = await loadHtmlImage(imageUrl);
  const labels = await resolveBrowserModelLabels({
    model,
    projectId,
    token,
    tfliteBytes,
  });
  const fallbackSize = modelImageSize(model);
  const spec = getModelInputSpec(compiled, fallbackSize);
  const inH = spec.height;
  const inW = spec.width;
  const colors = ANNOTATE_LITERT_COLORS;

  if (isClassificationModel(model)) {
    const prep = classificationDraw(img, inW, inH, {
      layout: spec.layout,
      normalize: 'imagenet',
    });
    const preds = await runClassification(compiled, prep.tensor, {
      labels,
      topK: 1,
      inputW: inW,
      inputH: inH,
      inputSpec: spec,
    });
    const top = preds[0];
    if (!top) {
      return { annotations: [], colors, source: 'litert' };
    }
    return {
      annotations: [{
        coords: [0, 0, 1, 1],
        conf: String(Number(top.score.toFixed(4))),
        label: top.label,
        type: 1,
        format: 'normalized_xywh',
      }],
      colors,
      source: 'litert',
    };
  }

  const prep = letterboxDraw(img, inW, inH, {
    layout: spec.layout,
    normalize: 'uint8',
    padColor: 114,
  });
  const detections = await runDetection(compiled, prep.tensor, {
    labels,
    confThreshold,
    inputW: inW,
    inputH: inH,
    inputSpec: spec,
  });
  const annotations = detections
    .map((det) => {
      const coords = detectionToNormalizedXywh(det, prep);
      if (coords[2] < 0.001 || coords[3] < 0.001) {
        return null;
      }
      return {
        coords,
        conf: String(Number(det.score.toFixed(4))),
        label: det.label,
        type: 1,
        format: 'normalized_xywh',
      };
    })
    .filter(Boolean);

  return { annotations, colors, source: 'litert' };
}
